<template>
  <v-card class="mt-4">
    <v-toolbar>
      <v-toolbar-title>
        <v-icon>mdi-video-outline</v-icon>
        {{ i18n.global.t('pages.vehicleInstall.eventVideos.title') }}
      </v-toolbar-title>
    </v-toolbar>
    <v-list class="scrollable">
      <v-list-item
        v-if="props.errorMessage"
        :title="props.errorMessage"
      >
      </v-list-item>
      <div
        v-else
        v-for="(vehicleEvent, index) in props.vehicleEvents"
        :key="index"
      >
        <v-list-item
          v-if="hasNoVideos(vehicleEvent)"
          :title="vehicleEvent.event_date"
        >
          {{ i18n.global.t(`pages.vehicleInstall.eventVideos.eventType.${vehicleEvent.event_type}`) }}
        </v-list-item>
        <v-list-group v-else>
          <template
            v-slot:activator="{ props }"
          >
            <v-list-item
              :title="vehicleEvent.event_date"
              v-bind="props"
            >
              <span class="d-flex align-center">
                {{ i18n.global.t(`pages.vehicleInstall.eventVideos.eventType.${vehicleEvent.event_type}`) }}
                <v-icon class="ml-1">mdi-video-outline</v-icon>
              </span>
            </v-list-item>
          </template>
          <v-list-item
            v-for="(video, index) in visibleVideos(vehicleEvent)"
            :key="index"
          >
            {{ i18n.global.t('pages.vehicleInstall.eventVideos.cameraType.' + video.labelKey) }}
            <video
              :src="getVideoUrl(vehicleEvent, video.field)"
              controls
              class="w-100 h-auto"
            >
            </video>
          </v-list-item>
        </v-list-group>
        <v-divider class="my-4"></v-divider>
      </div>
    </v-list>
  </v-card>
</template>

<script setup lang="ts">
import i18n from '@/lang/i18n';
import type GetVehicleEvent from '@/types/responses/getVehicleEvent';
import type VehicleInstallTranslations from '@/lang/ja/vehicleInstall';

const props = defineProps<{
  vehicleEvents: GetVehicleEvent[];
  errorMessage?: string;
}>();

type CameraLabel = keyof typeof VehicleInstallTranslations['eventVideos']['cameraType'];

type EventVidolist = {
  field: keyof GetVehicleEvent,
  labelKey: CameraLabel
}

const eventVideoList: EventVidolist[] = [
  { field: 'main_video_url', labelKey: 'cameraFront' },
  { field: 'sub_video_url', labelKey: 'cameraRear' },
  { field: 'inner_video_url', labelKey: 'cameraInner' },
  { field: 'side_video_url', labelKey: 'cameraSide' }
];

function getVideoUrl(event: GetVehicleEvent, field: keyof GetVehicleEvent): string | undefined {
  const url = event[field];
  if (url == null) {
    return undefined;
  }
  return String(url);
}

function visibleVideos(event: GetVehicleEvent): EventVidolist[] {
  return eventVideoList.filter(ev => !!getVideoUrl(event, ev.field));
}

function hasNoVideos(vehicleEvent: GetVehicleEvent) {
  return eventVideoList.every(ev => !getVideoUrl(vehicleEvent, ev.field));
}
</script>

<style scoped>
.scrollable {
  height: 70vh;
  overflow-y: auto;
}

@media (max-width: 840px) {
  .scrollable {
    height: 30vh;
  }
}
</style>
